/**
 * Model for Custom Image overlay feature
 * Allows designers to upload architectural files, drone imagery, etc.
 * as overlays on which they can draw roofs and use design capabilities
 */

export interface CustomImagePosition {
  x: number;
  y: number;
  z: number;
}

export interface CustomImageScale {
  width: number;
  height: number;
  aspectRatio?: number;
  // meshWidth and meshHeight store the actual Three.js mesh dimensions
  // for accurate restoration after merge/unmerge operations
  meshWidth?: number;
  meshHeight?: number;
}

export interface CustomImageCalibration {
  isCalibrated: boolean;
  pixelsDistance: number;
  actualDistance: number;
  actualDistanceFt?: number;
  point1?: CustomImagePosition;
  point2?: CustomImagePosition;
  scaleFactor?: number;
  scaleFactorApplied?: number;
}

export interface CustomImage {
  id: string;
  imageUrl: string;
  originalFileName: string;
  originalWidthPx?: number;
  originalHeightPx?: number;
  fileSize: number;
  fileType: string;
  position: CustomImagePosition;
  rotation: number; // 0-360 degrees
  opacity: number; // 0-100 percent
  scale: CustomImageScale;
  calibration: CustomImageCalibration;
  calibrated: boolean;
  visible: boolean;
  deleted: boolean;
  uploadSourceType: 'GoogleMap' | 'GoogleSolar' | 'NoMap';
  vertexIds: string[];
  createdAt: Date;
  updatedAt: Date;
  s3Url?: string;
}

/**
 * Default values for a new CustomImage
 */
export const DEFAULT_CUSTOM_IMAGE: Partial<CustomImage> = {
  position: { x: 0, y: 0, z: 6 }, // Above ground plane at z=5
  rotation: 0,
  opacity: 100,
  visible: true,
  deleted: false,
  calibration: {
    isCalibrated: false,
    pixelsDistance: 0,
    actualDistance: 0
  },
  vertexIds: []
};

/**
 * Implementation class for CustomImage with constructor
 */
export class CustomImageImpl implements CustomImage {
  id: string;
  imageUrl: string;
  originalFileName: string;
  originalWidthPx?: number;
  originalHeightPx?: number;
  fileSize: number;
  fileType: string;
  position: CustomImagePosition;
  rotation: number;
  opacity: number;
  scale: CustomImageScale;
  calibration: CustomImageCalibration;
  calibrated: boolean;
  visible: boolean;
  deleted: boolean;
  uploadSourceType: 'GoogleMap' | 'GoogleSolar' | 'NoMap';
  vertexIds: string[];
  createdAt: Date;
  updatedAt: Date;
  s3Url?: string;

  constructor(data: Partial<CustomImage>) {
    this.id = data.id || '';
    this.imageUrl = data.imageUrl || '';
    this.originalFileName = data.originalFileName || '';
    this.originalWidthPx = data.originalWidthPx;
    this.originalHeightPx = data.originalHeightPx;
    this.fileSize = data.fileSize || 0;
    this.fileType = data.fileType || '';
    this.position = data.position || { x: 0, y: 0, z: -4 };
    this.rotation = data.rotation || 0;
    this.opacity = data.opacity ?? 100;
    this.scale = data.scale || { width: 100, height: 100 };
    this.calibration = data.calibration || {
      isCalibrated: false,
      pixelsDistance: 0,
      actualDistance: 0
    };
    this.calibrated = data.calibrated ?? false;
    this.visible = data.visible ?? true;
    this.deleted = data.deleted ?? false;
    this.uploadSourceType = data.uploadSourceType || 'NoMap';
    this.vertexIds = data.vertexIds || [];
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
    this.s3Url = data.s3Url;
  }

  /**
   * Creates a CustomImage from a File object
   */
  static fromFile(file: File, uploadSourceType: 'GoogleMap' | 'GoogleSolar' | 'NoMap'): Promise<CustomImageImpl> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const customImage = new CustomImageImpl({
            id: crypto.randomUUID ? crypto.randomUUID() : `custom-image-${Date.now()}`,
            imageUrl: e.target?.result as string,
            originalFileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            scale: {
              width: img.width,
              height: img.height
            },
            uploadSourceType,
            createdAt: new Date(),
            updatedAt: new Date()
          });
          resolve(customImage);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Validates the file type
   */
  static isValidFileType(file: File): boolean {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return validTypes.includes(file.type.toLowerCase());
  }

  /**
   * Validates the file size (max 15MB)
   */
  static isValidFileSize(file: File): boolean {
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    return file.size <= maxSize;
  }

  /**
   * Validates both file type and size
   */
  static validateFile(file: File): { valid: boolean; error?: string } {
    if (!CustomImageImpl.isValidFileType(file)) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload a PNG, JPG, or JPEG file.'
      };
    }
    if (!CustomImageImpl.isValidFileSize(file)) {
      return {
        valid: false,
        error: 'File is too large. Maximum file size is 15MB.'
      };
    }
    return { valid: true };
  }
}
